import type { Topic } from '../api/exams'
import type { Section } from './Topics'

export interface TopicMembershipProps {
  /** One per Session of every Paper, in the Format's order, each with the Topics it examines. */
  sections: Section[]
  /** The Topics no Session examines yet, listed apart so they are not lost from view. */
  unplaced: Topic[]
  onOpen: (code: string) => void
}

/**
 * Which Session examines which Topic: one list per Paper -- per Session where the Exam splits
 * the Paper -- each Topic in definition order with its Subtopics under it, code and text.
 *
 * Plain markup rather than the Questions Manager's grid: there is nothing to sort, filter or
 * type into, only a list to read, and a Topic examined in two Sections is listed under both.
 * A Topic opens in the drawer, which is where its Sections are ticked.
 */
export function TopicMembership({ sections, unplaced, onOpen }: TopicMembershipProps) {
  return (
    <div className="mt-4 grid gap-6">
      {unplaced.length > 0 && (
        <TopicList
          heading="Under no Session"
          caption={null}
          topics={unplaced}
          onOpen={onOpen}
          className="border-red-700"
        />
      )}
      {sections.map((section) => (
        <TopicList
          key={section.key}
          heading={section.label}
          caption={section.paperName || null}
          topics={section.topics}
          onOpen={onOpen}
          className="border-ink-100"
        />
      ))}
    </div>
  )
}

function TopicList({
  heading,
  caption,
  topics,
  onOpen,
  className,
}: {
  heading: string
  caption: string | null
  topics: Topic[]
  onOpen: (code: string) => void
  className: string
}) {
  return (
    <section aria-label={heading} className={`rounded-lg border bg-card p-4 ${className}`}>
      <h3 className="font-heading text-h3 font-bold text-heading">
        {heading}
        {caption && <span className="ml-2 font-normal text-muted">{caption}</span>}
      </h3>

      {topics.length === 0 ? (
        <p className="mt-2 text-red-700">No Topic.</p>
      ) : (
        <ul className="mt-2 divide-y divide-ink-100">
          {topics.map((topic) => {
            const subtopics = subtopicsOf(topic)

            return (
              <li key={topic.code} className="py-2">
                <button
                  type="button"
                  className="text-left font-bold text-green-800 hover:underline"
                  onClick={() => onOpen(topic.code)}
                >
                  {topic.code} {topic.name}
                </button>
                {subtopics.length > 0 && (
                  <ol aria-label={`${topic.code} subtopics`} className="mt-1 grid gap-1 text-sm">
                    {subtopics.map((subtopic) => (
                      <li key={subtopic.code} className="flex gap-3">
                        <code className="w-14 shrink-0 text-muted">{subtopic.code}</code>
                        <span>{subtopic.text}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}

/**
 * A Topic's Label, as the API gives one: whatever follows the first hyphen of its code, so
 * P2-Prf is shown as Prf.
 */
export function labelOf(code: string): string {
  const hyphen = code.indexOf('-')

  return hyphen >= 0 && hyphen < code.length - 1 ? code.slice(hyphen + 1) : code
}

/**
 * A Topic's Subtopics with the codes the API derives for them: the Label, then a dot where the
 * Label ends in a digit (MM1.3) and nothing where it does not (Arg2).
 */
export function subtopicsOf(topic: Topic): { code: string; text: string }[] {
  const label = labelOf(topic.code)
  const dot = /[0-9]$/.test(label) ? '.' : ''

  return (topic.subtopics ?? []).map((text, at) => ({ code: `${label}${dot}${at + 1}`, text }))
}
